import { CommonModule } from "@angular/common"
import { Component, computed, effect, inject, OnInit, signal, untracked } from "@angular/core"
import { FormsModule } from "@angular/forms"
import { MatBadgeModule } from "@angular/material/badge"
import { MatButtonModule } from "@angular/material/button"
import { MatExpansionModule } from "@angular/material/expansion"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatIconModule } from "@angular/material/icon"
import { MatListModule } from "@angular/material/list"
import { MatMenuModule } from "@angular/material/menu"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { MatSelectModule } from "@angular/material/select"
import { MatSidenavModule } from "@angular/material/sidenav"
import { MatToolbarModule } from "@angular/material/toolbar"
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from "@angular/router"
import { filter } from "rxjs"
import { environment } from "../environments/environment"
import { NotificationCenterComponent } from "./core/components/notification-center/notification-center.component"
import { Entity } from "./core/models"
import { EntityFilterService, EntityService } from "./core/services"
import { AuthService } from "./core/services/auth.service"
import { ClerkService } from "./core/services/clerk.service"
import { ThemeService } from "./core/services/theme.service"
import { TransactionService } from "./core/services/transaction.service"
import { ChatWidgetComponent } from "./features/chat/chat-widget/chat-widget.component"

@Component({
  selector: "app-root",
  standalone: true,
  imports: [
    FormsModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatIconModule,
    MatBadgeModule,
    MatButtonModule,
    MatExpansionModule,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatSelectModule,
    MatFormFieldModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    NotificationCenterComponent,
    ChatWidgetComponent,
    CommonModule,
  ],
  templateUrl: "./app.html",
  styleUrl: "./app.css",
})
export class App implements OnInit {
  themeService = inject(ThemeService)
  private readonly entityService = inject(EntityService)
  private readonly entityFilterService = inject(EntityFilterService)
  private readonly transactionService = inject(TransactionService)
  private readonly router = inject(Router)
  readonly clerkService = inject(ClerkService)
  readonly authService = inject(AuthService)

  entities = signal<Entity[]>([])
  selectedEntityIds = signal<string[]>(this.entityFilterService.getSelectedEntityIds())
  pendingLinksCount = signal<number>(0)
  currentUser$ = this.clerkService.currentUser$

  // Nav accordion state – tracks which groups are expanded
  expandedGroups = signal<Set<string>>(new Set<string>())

  // Route-prefix → nav group mapping
  private readonly routeGroups: [string, string][] = [
    ["/transactions", "bookkeeping"],
    ["/invoices", "bookkeeping"],
    ["/documents", "bookkeeping"],
    ["/journal", "bookkeeping"],
    ["/contacts", "bookkeeping"],
    ["/mandates", "bookkeeping"],
    ["/analysis", "analytics"],
    ["/reports", "analytics"],
    ["/ledger", "analytics"],
    ["/accounts", "banking"],
    ["/institutes", "banking"],
    ["/balances", "banking"],
    ["/assets", "fixed-assets"],
    ["/loans", "fixed-assets"],
    ["/categories", "automation"],
    ["/rules", "automation"],
    ["/patterns", "automation"],
    ["/entities", "administration"],
    ["/email-ingestion", "administration"],
    ["/admin", "administration"],
    ["/style-guide", "administration"],
  ]

  private getGroupForUrl(url: string): string | null {
    for (const [prefix, group] of this.routeGroups) {
      if (url.startsWith(prefix)) return group
    }
    return null
  }

  isGroupExpanded(group: string): boolean {
    return this.expandedGroups().has(group)
  }

  onGroupExpanded(group: string, expanded: boolean): void {
    this.expandedGroups.update((groups) => {
      const next = new Set(groups)
      if (expanded) {
        next.add(group)
      } else {
        next.delete(group)
      }
      return next
    })
  }

  // Combine Clerk auth + Backend verification to prevent UI flicker
  // The UI will only show "Logged In" state when both are true
  isAuthenticated = computed(
    () => this.clerkService.isAuthenticated() && this.authService.isBackendVerified(),
  )

  constructor() {
    // Auto-expand the group that contains the current URL
    const initialGroup = this.getGroupForUrl(this.router.url)
    if (initialGroup) {
      this.expandedGroups.set(new Set([initialGroup]))
    }

    // Keep the active group expanded on navigation
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe((e) => {
      const navEnd = e as NavigationEnd
      const group = this.getGroupForUrl(navEnd.urlAfterRedirects)
      if (group) {
        this.expandedGroups.update((groups) => new Set([...groups, group]))
      }
    })

    effect(() => {
      // Use untracked for logic inside to avoid infinite loops if it reads signals
      // But we depend on isAuthenticated()
      if (this.isAuthenticated()) {
        untracked(() => {
          this.loadEntities()
          this.loadPendingLinksCount()
        })
      }
    })
  }

  async ngOnInit() {
    // Initialize Clerk
    await this.clerkService.initialize(environment.clerkPublishableKey)
  }

  loadPendingLinksCount() {
    this.transactionService.getPendingLinksCount().subscribe({
      next: (res) => this.pendingLinksCount.set(res.count),
      error: () => this.pendingLinksCount.set(0),
    })
  }

  loadEntities() {
    // Load entities for global filter
    this.entityService.getEntities().subscribe({
      next: (entities) => {
        this.entities.set(entities)
        // Restore selected entities from persisted filter
        const persistedIds = this.entityFilterService.getSelectedEntityIds()
        if (persistedIds.length > 0) {
          // Validate persisted IDs against loaded entities to prevent 404s
          const validIds = persistedIds.filter((id) => entities.some((e) => e.id === id))
          if (validIds.length > 0) {
            this.selectedEntityIds.set(validIds)
            // Update filter service if some were filtered out
            if (validIds.length !== persistedIds.length) {
              this.entityFilterService.setSelectedEntityIds(validIds)
            }
          } else {
            // If no valid IDs, clear filter
            this.selectedEntityIds.set([])
            this.entityFilterService.setSelectedEntityIds([])
          }
        }
      },
      error: (error) => {
        console.error("Failed to load entities:", error)
      },
    })
  }

  getEntityName(entityIds: string[]): string {
    if (!entityIds || entityIds.length === 0) return "All Entities"
    if (entityIds.length === 1) {
      const entity = this.entities().find((e) => e.id === entityIds[0])
      return entity ? entity.name : "Unknown"
    }
    return `${entityIds.length} Entities`
  }

  onEntityChange(entityIds: string[]): void {
    this.selectedEntityIds.set(entityIds)
    this.entityFilterService.setSelectedEntityIds(entityIds)
  }

  async logout(): Promise<void> {
    await this.clerkService.signOut()
  }

  async openUserProfile(): Promise<void> {
    await this.clerkService.openUserProfile()
  }
}
